import * as React from 'react';
import { isDenied, isPending, type PermissionProbe } from './permission-verdict.ts';
import {
  Plus,
  Download,
  Columns3,
  MoreHorizontal,
  Filter as FilterIcon,
  Lock,
  Trash2,
  Save,
  BookMarked,
} from 'lucide-react';
import {
  useUrlFilterState,
  useNumberFormat,
  useCurrencyFormat,
  useDateFormat,
  usePlantDateFormat,
} from '@tyndo/hooks';

import { downloadCsv, type CsvColumn } from '../lib/csv';
import { Button } from '../primitives/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuCheckboxItem,
} from '../primitives/dropdown-menu';
import { Badge } from '../primitives/badge';
import { Input } from '../primitives/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../primitives/popover';
import { cn } from '../lib/utils';
import { ConfirmActionDialog, type ConfirmReasonMode } from './confirm-action-dialog';

/**
 * Canonical list-view archetype contract.
 *
 * Every "first-thing-an-operator-hits" list view composes
 * `useListArchetype()` and renders the returned `<ListArchetypeToolbar />`
 * above the master surface.
 *
 * The hook + toolbar bundle the 10 audit-mandated affordances:
 *
 *   1.  create               — primary Create button (permission-gated)
 *   2.  bulk-actions         — bar shown when `selectedIds.length > 0`
 *   3.  saved-filters        — chips persisted in localStorage by tenant+user+viewId
 *   4.  column-management    — checkbox menu, persisted by tenant+user+viewId
 *   5.  export               — CSV / JSON menu wired to `onExport`
 *   6.  pagination           — `page` / `pageSize` state (header summary)
 *   7.  empty-state          — caller renders `<EmptyState />`; helpers
 *                              `archetype.isEmpty` short-circuits
 *   8.  error-state          — `archetype.errorView` returns ErrorState JSX
 *   9.  permission-denied    — uses `usePermission()` result; toolbar
 *                              swaps in <Lock /> banner when forbidden
 *  10. row-actions           — kebab menu generator (`rowActions(row)`)
 *
 * The audit (`scripts/audit-list-view-archetype-coverage.mjs`) detects this
 * primitive by the testid prefix `list-archetype-`. Views that compose this
 * hook automatically count "full" without spreading the 10 patterns
 * inline.
 */

const STORAGE_PREFIX = 'tyndo:list:';

/**
 * Default ISO-4217 currency for the archetype's currency formatter. A caller or
 * tenant can override per list via {@link UseListArchetypeOpts.currency} (or the
 * archetype provider). Kept as USD so existing consumers see no behaviour change.
 */
export const DEFAULT_LIST_CURRENCY = 'USD';

/**
 * Identity used to namespace persisted list preferences. Without this, one
 * user's saved filters / column prefs would leak to another user (or another
 * tenant) on a shared browser, since the keys were previously scoped only by
 * `viewId`. Resolved from the auth/session context the shell wires via
 * {@link _registerListArchetypeIdentityHook}; falls back to `anonymous` so
 * the hook still works in storybook/tests with no auth.
 */
export interface ListArchetypeIdentity {
  userId: string;
  tenantId: string;
}

type UseIdentityLike = () => ListArchetypeIdentity | null;

let _useIdentity: UseIdentityLike | null = null;
export function _registerListArchetypeIdentityHook(fn: UseIdentityLike) {
  _useIdentity = fn;
}

/** Sanitize an id segment so it can't break the `:`-delimited key. */
function keySegment(value: string | undefined | null): string {
  if (!value) return 'anonymous';
  return value.replace(/[^A-Za-z0-9_-]/g, '');
}

export interface SavedFilterEntry {
  id: string;
  label: string;
  description?: string;
}

/**
 * A user-named, persisted snapshot of a list's filter state. Captured via the
 * archetype's saved-views slot and restored on apply. The archetype always
 * captures its *own* managed state (active saved-filter + page size); a view
 * can carry its bespoke filter state (search box, status select, date range)
 * through the opaque `extra` bag by wiring `captureExtraState` /
 * `onApplyExtraState` on the hook options.
 */
export interface ListArchetypeSavedView {
  id: string;
  name: string;
  /** Archetype-managed state captured at save time. */
  state: {
    activeFilterId: string | null;
    pageSize: number;
  };
  /** Opaque per-view filter bag (search/status/date/etc.). */
  extra?: Record<string, unknown>;
}

export interface ListArchetypeColumn {
  id: string;
  label: string;
  /** When `false`, the column is hidden by default. */
  defaultVisible?: boolean;
  /** When `true`, the column cannot be hidden (e.g. the primary id column). */
  required?: boolean;
}

/**
 * Confirmation gate for an irreversible list action. When present on a row or
 * bulk action, the archetype routes the action through the shared
 * {@link ConfirmActionDialog} before `onRun` fires — so teams can no longer
 * skip the confirm gate on a server-side delete/abort/void by merely flagging
 * the action `destructive` (styling only). Mirrors the `requiresConfirm` gate
 * the console archetype already owns for `ConsoleCommand`.
 *
 * The dialog's typed reason (when `reason` is `optional`/`required`) is passed
 * back to `onRun` via the second `detail` argument so GxP call sites can carry
 * a justification into the server audit.
 */
export interface ListActionConfirm {
  /**
   * Heading, e.g. "Abort batch BR-1042?". Should name the entity and read as a
   * question.
   */
  title: React.ReactNode;
  /** The action verb on the confirm button, e.g. "Abort". Defaults to the action label. */
  confirmVerb?: string;
  /**
   * Body copy under the title — name the irreversible consequence, e.g.
   * "This cannot be undone."
   */
  description?: React.ReactNode;
  /** Typed-reason field mode. Default `hidden`. */
  reason?: ConfirmReasonMode;
  /** Label for the reason field when shown. */
  reasonLabel?: string;
  /** Whether the confirm button uses destructive styling. Default true. */
  destructive?: boolean;
}

/** Detail passed to `onRun` after a confirmed action. */
export interface ListActionRunDetail {
  /** Typed reason captured by the confirm dialog (empty when no field shown). */
  reason: string;
}

export interface ListBulkAction {
  id: string;
  label: string;
  /** Optional icon node. */
  icon?: React.ReactNode;
  /** Destructive actions get a red variant. */
  destructive?: boolean;
  /**
   * Confirmation gate. Three forms:
   *
   *  - A {@link ListActionConfirm} object → routes through
   *    {@link ConfirmActionDialog} with that exact copy (richer dialog).
   *  - `false` → opt out of confirmation entirely (fire immediately), even
   *    when `destructive` is set. Use only when the call site already drives
   *    its own confirmation (e.g. it opens a bespoke dialog in `onRun`) so the
   *    auto-confirm default below would otherwise double-prompt.
   *  - Omitted → a `destructive` action gets an **auto-generated** default
   *    confirm ("{label} {n} items? This can't be undone."); a non-destructive
   *    action fires immediately.
   */
  confirm?: ListActionConfirm | false;
  /** Alias for `confirm: false`. Opts a destructive action out of auto-confirm. */
  skipConfirm?: boolean;
  onRun: (selectedIds: string[], detail?: ListActionRunDetail) => void | Promise<void>;
}

export interface ListRowAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  destructive?: boolean;
  /**
   * Confirmation gate. Three forms:
   *
   *  - A {@link ListActionConfirm} object → routes through
   *    {@link ConfirmActionDialog} with that exact copy (richer dialog).
   *  - `false` → opt out of confirmation entirely (fire immediately), even
   *    when `destructive` is set. Use only when the call site already drives
   *    its own confirmation (e.g. it opens a bespoke dialog in `onRun`).
   *  - Omitted → a `destructive` action gets an **auto-generated** default
   *    confirm ("{label}? This can't be undone."); a non-destructive action
   *    fires immediately.
   */
  confirm?: ListActionConfirm | false;
  /** Alias for `confirm: false`. Opts a destructive action out of auto-confirm. */
  skipConfirm?: boolean;
  onRun: (rowId: string, detail?: ListActionRunDetail) => void | Promise<void>;
}

export interface UseListArchetypeOpts {
  /** Stable identifier used as the localStorage key root. */
  viewId: string;
  /** Resource + action passed to the permission check. */
  permission?: { resource: string; action: string };
  /** Pre-canned filters shown as chips. */
  savedFilters?: SavedFilterEntry[];
  /** Default-active filter id when no localStorage entry exists. */
  defaultFilterId?: string | null;
  /** Column descriptors used by the column-visibility menu. */
  columns?: ListArchetypeColumn[];
  /** Default page size for paginated list consumers. */
  defaultPageSize?: number;
  /** Imperatively bypass the permission gate (used in storybook/tests). */
  alwaysAllow?: boolean;
  /**
   * Explicit identity used to namespace persisted preferences. When omitted
   * the hook resolves it from the registered identity hook, falling back to
   * an anonymous (non-leaking) namespace.
   */
  identity?: ListArchetypeIdentity | null;
  /**
   * Serialize the archetype's own filter state (active saved-filter, page,
   * page size) to the URL query string so a filtered list is bookmarkable,
   * shareable, and survives a refresh.
   *
   * Defaults to **on** — the keys are namespaced by `viewId` (`{viewId}.f`,
   * `{viewId}.pg`, `{viewId}.ps`) so two archetype lists mounted on one route
   * never collide, and the values default to empty so an un-filtered list adds
   * nothing to the URL (opt-out-safe). Pass `false` to disable on a surface
   * that owns its own URL scheme. SSR / no-`window` contexts degrade to local
   * state automatically.
   */
  urlState?: boolean;
  /**
   * Capture the *view-owned* filter state (search text, status select, date
   * range, …) when the operator saves a named view. The returned bag is stored
   * verbatim and handed back to {@link onApplyExtraState} on apply. Omit when
   * the archetype's own state is the whole filter surface.
   */
  captureExtraState?: () => Record<string, unknown>;
  /** Restore the view-owned filter bag captured by {@link captureExtraState}. */
  onApplyExtraState?: (extra: Record<string, unknown> | undefined) => void;
  /**
   * ISO-4217 currency code the archetype's default currency formatter uses.
   * Overrides the {@link DEFAULT_LIST_CURRENCY} (USD) so a EUR/JPY tenant sees
   * the right symbol without hand-wiring `useCurrencyFormat`. Defaults to USD,
   * so existing callers are unaffected.
   */
  currency?: string;
}

export interface UseListArchetypeResult {
  /** Saved-filter selection. */
  activeFilterId: string | null;
  setActiveFilterId: (id: string | null) => void;
  savedFilters: SavedFilterEntry[];

  /** User-named saved views (filter snapshots) for this list. */
  savedViews: ListArchetypeSavedView[];
  /** Capture the current filter state under `name` and persist it. */
  saveView: (name: string) => void;
  /** Restore a saved view's filter state. */
  applyView: (view: ListArchetypeSavedView) => void;
  /** Remove a saved view by id. */
  deleteView: (id: string) => void;
  /**
   * Ready-made saved-views dropdown to drop into `<FilterBar savedViewsSlot>`.
   * Self-contained (capture / apply / delete + a name prompt); pass `null` to
   * the `savedViewsSlot` prop to opt a list out.
   */
  savedViewsSlot: React.ReactNode;

  /** Column-visibility selection (id → boolean). */
  visibleColumns: Record<string, boolean>;
  setColumnVisible: (id: string, visible: boolean) => void;
  resetColumns: () => void;

  /** Selected row ids (multi-select bulk pattern). */
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
  clearSelection: () => void;
  toggleSelected: (id: string) => void;

  /** Pagination state. */
  page: number;
  setPage: (n: number) => void;
  pageSize: number;
  setPageSize: (n: number) => void;

  /** Permission gate. */
  permissionDenied: boolean;
  permissionLoading: boolean;

  /**
   * Default locale-aware formatters, so a list view renders numbers, currency,
   * and dates consistently without re-wiring `useNumberFormat` /
   * `useCurrencyFormat` / `useDateFormat` per view. `format.currency` honours
   * the archetype's `currency` option (default USD). `format.plantDate` renders
   * timestamps in the active plant's timezone (see `usePlantTimeZone`).
   */
  format: ListArchetypeFormatters;

  /**
   * Default CSV export handler — builds a hardened, formula-injection-safe CSV
   * (mirrors backend `Tyndo.Core.Text.Csv`) from column defs + row data and
   * triggers a browser download. Wire it as the toolbar's `onExport('csv')` (or
   * pass `csvExport` to the hook to have the toolbar do it automatically).
   */
  downloadCsv: <Row>(
    data: readonly Row[],
    columns: ReadonlyArray<CsvColumn<Row>>,
    filename?: string,
  ) => void;

  /** Convenience: build the `data-testid` for an archetype affordance. */
  testId: (suffix: string) => string;
}

/** Locale-aware default formatters bundled by {@link useListArchetype}. */
export interface ListArchetypeFormatters {
  /** Format a number in the active locale. */
  number: (value: number) => string;
  /** Format a currency amount using the archetype's `currency` option. */
  currency: (value: number) => string;
  /** Format a date/time in the active locale (browser timezone). */
  date: (value: Date | number | string | null | undefined) => string;
  /** Format a date/time in the active plant's timezone. */
  plantDate: (value: Date | number | string | null | undefined) => string;
}

type PermissionCheckResult = PermissionProbe;

/**
 * Permission hook is wired lazily so the archetype primitive does not pull
 * `@tyndo/hooks` into the ui-kit graph. Consumers pass `permission` and the
 * hook will optimistically allow access until they wire it themselves.
 */
type UsePermissionLike = (resource: string, action: string) => PermissionCheckResult;

let _usePermission: UsePermissionLike | null = null;
export function _registerListArchetypePermissionHook(fn: UsePermissionLike) {
  _usePermission = fn;
}

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota; ignore */
  }
}

/** Default permission stub — always allow. Real apps register the hook. */
function defaultPermission(_resource: string, _action: string): PermissionCheckResult {
  return { allowed: true, loading: false };
}

export function useListArchetype(opts: UseListArchetypeOpts): UseListArchetypeResult {
  const {
    viewId,
    permission,
    savedFilters = [],
    defaultFilterId = null,
    columns = [],
    defaultPageSize = 25,
    alwaysAllow,
    identity: identityOverride,
    urlState = true,
    captureExtraState,
    onApplyExtraState,
    currency = DEFAULT_LIST_CURRENCY,
  } = opts;

  // Resolve identity (explicit override > registered hook > anonymous) and
  // build a per-tenant, per-user key namespace so preferences don't leak
  // across users sharing a browser. Existing single-viewId keys are simply
  // ignored — preferences start fresh per user rather than crashing.
  const identityHook = _useIdentity ?? (() => null);
  const resolvedIdentity = identityOverride ?? identityHook();
  const tenantSeg = keySegment(resolvedIdentity?.tenantId);
  const userSeg = keySegment(resolvedIdentity?.userId);
  const keyRoot = `${STORAGE_PREFIX}${tenantSeg}:${userSeg}:${viewId}`;

  const filterKey = keyRoot + ':filters';
  const columnKey = keyRoot + ':columns';
  const pageKey = keyRoot + ':pageSize';
  const viewsKey = keyRoot + ':views';

  // ─── URL-persisted filter/page state ────────────────────────────
  // The archetype's own filter surface (active saved-filter + page + page
  // size) is serialized to the URL so the list is bookmarkable / shareable.
  // Keys are namespaced by `viewId` so two archetype lists on one route never
  // collide. The empty-string / "0" / default-page-size defaults are pruned
  // from the URL, so an un-filtered list adds nothing to the query string.
  //
  // localStorage still seeds the *initial* value (a returning user keeps their
  // last filter even with a bare URL) and is written through on change, so the
  // pre-existing persistence contract is preserved. When `urlState` is off the
  // hook never touches the URL and behaves exactly as before.
  const urlDefaults = React.useMemo(
    () => ({
      f: readJson<string | null>(filterKey, defaultFilterId) ?? '',
      pg: '0',
      ps: String(readJson<number>(pageKey, defaultPageSize)),
    }),
    // Seeded once from storage; later storage writes flow through the setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [viewId],
  );
  const [urlFilterState, setUrlFilterState] = useUrlFilterState(urlDefaults, {
    prefix: `${keySegment(viewId)}.`,
  });

  // ─── Saved filters ──────────────────────────────────────────────
  // Local mirror used when `urlState` is disabled (no URL writes at all).
  const [localFilterId, setLocalFilterIdState] = React.useState<string | null>(
    () => readJson<string | null>(filterKey, defaultFilterId),
  );
  const activeFilterId = urlState
    ? urlFilterState.f === ''
      ? null
      : urlFilterState.f
    : localFilterId;
  const setActiveFilterId = React.useCallback(
    (id: string | null) => {
      setLocalFilterIdState(id);
      writeJson(filterKey, id);
      if (urlState) {
        setUrlFilterState((prev) => ({ ...prev, f: id ?? '', pg: '0' }));
      }
    },
    [filterKey, urlState, setUrlFilterState],
  );

  // ─── Column management ──────────────────────────────────────────
  const initialColumnVisibility = React.useMemo(() => {
    const persisted = readJson<Record<string, boolean> | null>(columnKey, null);
    const defaults: Record<string, boolean> = {};
    for (const c of columns) {
      defaults[c.id] = c.defaultVisible !== false;
    }
    return persisted ? { ...defaults, ...persisted } : defaults;
  }, [columnKey, columns]);

  const [visibleColumns, setVisibleColumnsState] = React.useState<
    Record<string, boolean>
  >(initialColumnVisibility);

  // React to later `columns` prop changes without clobbering the user's
  // explicit toggles: newly-added columns adopt their default visibility,
  // removed columns are dropped, and any column the user has already toggled
  // keeps its current visibility. Keyed on the column-id signature so it only
  // re-runs when the column set actually changes.
  const columnSignature = columns.map((c) => `${c.id}:${c.defaultVisible !== false ? 1 : 0}`).join('|');
  React.useEffect(() => {
    setVisibleColumnsState((prev) => {
      const next: Record<string, boolean> = {};
      let changed = false;
      for (const c of columns) {
        if (Object.prototype.hasOwnProperty.call(prev, c.id)) {
          // Retain the user's existing choice for known columns.
          next[c.id] = prev[c.id] as boolean;
        } else {
          // New column — adopt its declared default.
          next[c.id] = c.defaultVisible !== false;
          changed = true;
        }
      }
      // Detect removed columns (present in prev but not in the new set).
      if (!changed && Object.keys(prev).length !== Object.keys(next).length) {
        changed = true;
      }
      return changed ? next : prev;
    });
    // `columns` is intentionally excluded in favour of the stable signature.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnSignature]);

  const setColumnVisible = React.useCallback(
    (id: string, visible: boolean) => {
      setVisibleColumnsState((prev) => {
        const next = { ...prev, [id]: visible };
        writeJson(columnKey, next);
        return next;
      });
    },
    [columnKey],
  );

  const resetColumns = React.useCallback(() => {
    const defaults: Record<string, boolean> = {};
    for (const c of columns) {
      defaults[c.id] = c.defaultVisible !== false;
    }
    setVisibleColumnsState(defaults);
    writeJson(columnKey, defaults);
  }, [columnKey, columns]);

  // ─── Multi-select / bulk actions ────────────────────────────────
  const [selectedIds, setSelectedIdsState] = React.useState<string[]>([]);
  const clearSelection = React.useCallback(() => setSelectedIdsState([]), []);
  const toggleSelected = React.useCallback((id: string) => {
    setSelectedIdsState((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }, []);
  const setSelectedIds = React.useCallback((ids: string[]) => {
    setSelectedIdsState(ids);
  }, []);

  // ─── Pagination ─────────────────────────────────────────────────
  // Local mirrors used when `urlState` is disabled.
  const [localPage, setLocalPage] = React.useState(0);
  const [localPageSize, setLocalPageSizeState] = React.useState<number>(() =>
    readJson<number>(pageKey, defaultPageSize),
  );

  const page = urlState
    ? Math.max(0, Number.parseInt(urlFilterState.pg, 10) || 0)
    : localPage;
  const pageSize = urlState
    ? Math.max(1, Number.parseInt(urlFilterState.ps, 10) || defaultPageSize)
    : localPageSize;

  const setPage = React.useCallback(
    (n: number) => {
      setLocalPage(n);
      if (urlState) {
        setUrlFilterState((prev) => ({ ...prev, pg: String(Math.max(0, n)) }));
      }
    },
    [urlState, setUrlFilterState],
  );
  const setPageSize = React.useCallback(
    (n: number) => {
      setLocalPageSizeState(n);
      writeJson(pageKey, n);
      if (urlState) {
        // Changing the page size resets to the first page.
        setUrlFilterState((prev) => ({ ...prev, ps: String(n), pg: '0' }));
      }
    },
    [pageKey, urlState, setUrlFilterState],
  );

  // ─── Saved views ────────────────────────────────────────────────
  // Named snapshots of the filter surface, persisted per tenant+user+viewId.
  // Each view captures the archetype's own state plus any view-owned `extra`
  // bag, and restores both on apply.
  const [savedViews, setSavedViews] = React.useState<ListArchetypeSavedView[]>(
    () => readJson<ListArchetypeSavedView[]>(viewsKey, []),
  );
  const persistViews = React.useCallback(
    (next: ListArchetypeSavedView[]) => {
      setSavedViews(next);
      writeJson(viewsKey, next);
    },
    [viewsKey],
  );
  const saveView = React.useCallback(
    (name: string) => {
      const trimmed = name.trim();
      if (!trimmed) return;
      const view: ListArchetypeSavedView = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: trimmed,
        state: { activeFilterId, pageSize },
        extra: captureExtraState?.(),
      };
      persistViews([...savedViews, view]);
    },
    [activeFilterId, pageSize, captureExtraState, savedViews, persistViews],
  );
  const applyView = React.useCallback(
    (view: ListArchetypeSavedView) => {
      setActiveFilterId(view.state.activeFilterId);
      setPageSize(view.state.pageSize);
      setPage(0);
      onApplyExtraState?.(view.extra);
    },
    [setActiveFilterId, setPageSize, setPage, onApplyExtraState],
  );
  const deleteView = React.useCallback(
    (id: string) => {
      persistViews(savedViews.filter((v) => v.id !== id));
    },
    [savedViews, persistViews],
  );

  const savedViewsSlot = (
    <ListArchetypeSavedViews
      views={savedViews}
      onSave={saveView}
      onApply={applyView}
      onDelete={deleteView}
      testId={`list-archetype-saved-views-${keySegment(viewId)}`}
    />
  );

  // ─── Permission gate ────────────────────────────────────────────
  const hook = _usePermission ?? defaultPermission;
  const probe = hook(
    permission?.resource ?? '__noop__',
    permission?.action ?? 'read',
  );
  // Denial requires an affirmative negative verdict — see permission-verdict.ts.
  const gated = Boolean(permission) && !alwaysAllow;
  const permissionDenied = isDenied(probe, gated);
  const permissionLoading = isPending(probe, gated);

  // ─── Default formatters ─────────────────────────────────────────
  // Locale-aware Intl formatters + a plant-timezone date formatter, so every
  // list view renders numbers/currency/dates consistently for free. Currency
  // honours the `currency` option (default USD); the plant-date formatter reads
  // the active plant timezone via the registered resolver (browser-local when
  // none is registered — no behaviour change for existing callers).
  const numberFormat = useNumberFormat();
  const currencyFormat = useCurrencyFormat(currency);
  const dateFormat = useDateFormat({ dateStyle: 'medium', timeStyle: 'short' });
  const plantDate = usePlantDateFormat({ dateStyle: 'medium', timeStyle: 'short' });
  const format = React.useMemo<ListArchetypeFormatters>(
    () => ({
      number: (value: number) => numberFormat.format(value),
      currency: (value: number) => currencyFormat.format(value),
      date: (value: Date | number | string | null | undefined) => {
        if (value == null || value === '') return '';
        const d = value instanceof Date ? value : new Date(value);
        return Number.isNaN(d.getTime()) ? '' : dateFormat.format(d);
      },
      plantDate,
    }),
    [numberFormat, currencyFormat, dateFormat, plantDate],
  );

  // ─── Default CSV export ─────────────────────────────────────────
  // Routes column defs + rows through the hardened, formula-injection-safe
  // escaper (mirrors backend Tyndo.Core.Text.Csv) and triggers a download, so
  // no list view hand-rolls (and under-escapes) its own CSV.
  const downloadCsvHandler = React.useCallback(
    <Row,>(
      data: readonly Row[],
      columns: ReadonlyArray<CsvColumn<Row>>,
      filename?: string,
    ) => {
      downloadCsv(data, { columns, filename: filename ?? `${keySegment(viewId)}.csv` });
    },
    [viewId],
  );

  const testId = React.useCallback(
    (suffix: string) => `list-archetype-${suffix}`,
    [],
  );

  return {
    activeFilterId,
    setActiveFilterId,
    savedFilters,
    savedViews,
    saveView,
    applyView,
    deleteView,
    savedViewsSlot,
    visibleColumns,
    setColumnVisible,
    resetColumns,
    selectedIds,
    setSelectedIds,
    clearSelection,
    toggleSelected,
    page,
    setPage,
    pageSize,
    setPageSize,
    permissionDenied,
    permissionLoading,
    format,
    downloadCsv: downloadCsvHandler,
    testId,
  };
}

// ──────────────────────────────────────────────────────────────────
// Saved-views dropdown — the ready-made `savedViewsSlot` node returned
// by the hook. Self-contained: list / apply / delete + a name prompt.
// ──────────────────────────────────────────────────────────────────

interface ListArchetypeSavedViewsProps {
  views: ListArchetypeSavedView[];
  onSave: (name: string) => void;
  onApply: (view: ListArchetypeSavedView) => void;
  onDelete: (id: string) => void;
  testId: string;
}

function ListArchetypeSavedViews({
  views,
  onSave,
  onApply,
  onDelete,
  testId,
}: ListArchetypeSavedViewsProps) {
  const [open, setOpen] = React.useState(false);
  const [naming, setNaming] = React.useState(false);
  const [name, setName] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (naming) inputRef.current?.focus();
  }, [naming]);

  React.useEffect(() => {
    if (!open) {
      setNaming(false);
      setName('');
    }
  }, [open]);

  const commit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSave(trimmed);
    setName('');
    setNaming(false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2"
          data-testid={testId}
          aria-label="Saved views"
        >
          <BookMarked className="mr-1 h-3.5 w-3.5" aria-hidden />
          Views
          {views.length > 0 ? (
            <Badge variant="muted" className="ml-1.5 text-[10px]">
              {views.length}
            </Badge>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-2">
        {naming ? (
          <div className="space-y-2">
            <label
              htmlFor={`${testId}-name`}
              className="text-xs font-medium text-muted-foreground"
            >
              Name this view
            </label>
            <Input
              ref={inputRef}
              id={`${testId}-name`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commit();
                if (e.key === 'Escape') setNaming(false);
              }}
              placeholder="e.g. Open · high priority"
              aria-label="View name"
              className="h-8 text-sm"
            />
            <div className="flex justify-end gap-1.5">
              <Button
                size="sm"
                variant="ghost"
                className="h-7"
                onClick={() => setNaming(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="h-7"
                onClick={commit}
                disabled={!name.trim()}
                data-testid={`${testId}-save`}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-start"
              onClick={() => setNaming(true)}
              data-testid={`${testId}-new`}
            >
              <Save className="mr-2 h-3.5 w-3.5" aria-hidden />
              Save current view
            </Button>
            <div className="my-1 border-t" />
            {views.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                No saved views yet.
              </p>
            ) : (
              <ul className="max-h-56 space-y-0.5 overflow-auto">
                {views.map((v) => (
                  <li key={v.id} className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 flex-1 justify-start truncate"
                      onClick={() => {
                        onApply(v);
                        setOpen(false);
                      }}
                      data-testid={`${testId}-apply-${v.id}`}
                    >
                      {v.name}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => onDelete(v.id)}
                      aria-label={`Delete view ${v.name}`}
                      data-testid={`${testId}-delete-${v.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ──────────────────────────────────────────────────────────────────
// Companion components
// ──────────────────────────────────────────────────────────────────

export interface ListArchetypeToolbarProps {
  archetype: UseListArchetypeResult;
  columns?: ListArchetypeColumn[];

  /** Create button. */
  onCreate?: () => void;
  createLabel?: string;
  canCreate?: boolean;

  /** Export menu. */
  onExport?: (format: 'csv' | 'json' | 'pdf') => void;
  exportFormats?: Array<'csv' | 'json' | 'pdf'>;

  /** Bulk actions surfaced when `selectedIds.length > 0`. */
  bulkActions?: ListBulkAction[];

  /** Pagination total count for the summary text. */
  totalCount?: number;

  /** Optional className. */
  className?: string;
}

/**
 * Compact toolbar that renders the create/export/columns/bulk
 * affordances. Designed to slot into existing master-detail headers
 * without taking over the layout.
 */
export function ListArchetypeToolbar({
  archetype,
  columns,
  onCreate,
  createLabel = 'Create',
  canCreate = true,
  onExport,
  exportFormats = ['csv', 'json'],
  bulkActions,
  totalCount,
  className,
}: ListArchetypeToolbarProps) {
  const t = archetype.testId;
  const hasSelection = archetype.selectedIds.length > 0;
  const hasFilters = archetype.savedFilters.length > 0;
  const hasColumns = (columns?.length ?? 0) > 0;

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 border-b bg-muted/20 px-3 py-2 text-xs',
        className,
      )}
      data-testid={t('toolbar')}
      role="region"
      aria-label="List actions"
    >
      {/* Saved filters */}
      {hasFilters ? (
        <div
          className="flex items-center gap-1"
          data-testid="filter-preset"
          aria-label="Saved filters"
        >
          <FilterIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <Button
            variant={archetype.activeFilterId == null ? 'secondary' : 'ghost'}
            size="sm"
            className="h-7 px-2"
            onClick={() => archetype.setActiveFilterId(null)}
            data-testid={t('filter-all')}
          >
            All
          </Button>
          {archetype.savedFilters.map((f) => (
            <Button
              key={f.id}
              variant={archetype.activeFilterId === f.id ? 'secondary' : 'ghost'}
              size="sm"
              className="h-7 px-2"
              onClick={() => archetype.setActiveFilterId(f.id)}
              title={f.description}
              data-testid={t(`filter-${f.id}`)}
            >
              {f.label}
            </Button>
          ))}
        </div>
      ) : null}

      <div className="ml-auto flex flex-wrap items-center gap-1">
        {totalCount != null ? (
          <span
            className="mr-2 text-muted-foreground"
            data-testid={t('pagination-summary')}
          >
            {totalCount} record{totalCount === 1 ? '' : 's'}
            {archetype.pageSize > 0 && totalCount > archetype.pageSize ? (
              <>
                {' · '}page {archetype.page + 1} of{' '}
                {Math.max(1, Math.ceil(totalCount / archetype.pageSize))}
              </>
            ) : null}
          </span>
        ) : null}

        {/* Column-visibility menu */}
        {hasColumns ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                data-testid="column-picker"
                aria-label="Manage columns"
              >
                <Columns3 className="mr-1 h-3.5 w-3.5" aria-hidden />
                Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[12rem]">
              <DropdownMenuLabel>Visible columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {columns!.map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={archetype.visibleColumns[c.id] !== false}
                  disabled={c.required}
                  onCheckedChange={(v) =>
                    archetype.setColumnVisible(c.id, Boolean(v))
                  }
                  data-testid={`column-toggle-${c.id}`}
                >
                  {c.label}
                  {c.required ? (
                    <Badge variant="muted" className="ml-2 text-[10px]">
                      required
                    </Badge>
                  ) : null}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => archetype.resetColumns()}
                data-testid={t('columns-reset')}
              >
                Reset to defaults
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {/* Export menu */}
        {onExport ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-7"
                aria-label="Export"
                data-testid={t('export')}
              >
                <Download className="mr-1 h-3.5 w-3.5" aria-hidden />
                Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {exportFormats.map((fmt) => (
                <DropdownMenuItem
                  key={fmt}
                  onClick={() => onExport(fmt)}
                  data-testid={t(`export-${fmt}`)}
                >
                  Export as {fmt.toUpperCase()}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}

        {/* Create */}
        {onCreate && canCreate ? (
          <Button
            size="sm"
            className="h-7"
            onClick={onCreate}
            data-testid={t('create')}
          >
            <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
            {createLabel}
          </Button>
        ) : null}
      </div>

      {/* Bulk actions bar — separate row, full width when active */}
      {hasSelection && bulkActions && bulkActions.length > 0 ? (
        <div
          className="mt-1 flex w-full items-center gap-2 rounded-md border border-primary/40 bg-primary/5 px-2 py-1"
          data-testid={t('bulk-actions')}
          role="region"
          aria-label="Bulk actions"
        >
          <span className="font-medium">
            {archetype.selectedIds.length} selected
          </span>
          <div className="ml-auto flex items-center gap-1">
            {bulkActions.map((a) => (
              <BulkActionButton
                key={a.id}
                action={a}
                selectedIds={archetype.selectedIds}
                testId={t(`bulk-${a.id}`)}
              />
            ))}
            <Button
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={archetype.clearSelection}
              data-testid={t('bulk-clear')}
            >
              Clear
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Default-confirm resolution
//
// Destructive list actions confirm by DEFAULT. The audit found dozens of
// `destructive: true` row/bulk actions firing irreversible mutations with no
// guard. Rather than wire a `confirm:` block into every one of the 130+ list
// views, the archetype now synthesizes a generic confirm for any destructive
// action that doesn't supply its own. Call sites keep full control:
//
//   • explicit `confirm: {…}`     → that richer dialog (unchanged)
//   • `confirm: false` / `skipConfirm`
//                                 → no dialog, fire immediately (opt-out, for
//                                   call sites that drive their own confirm)
//   • `destructive: true`, no confirm
//                                 → auto-generated default confirm (the fix)
//   • non-destructive, no confirm → fire immediately (unchanged)
//
// All four paths route through the SAME ConfirmActionDialog the explicit
// `confirm` option already used, so accessibility (focus trap, aria) and the
// single-fire settle guard are shared.
// ──────────────────────────────────────────────────────────────────

interface ResolvedConfirm extends ListActionConfirm {
  /** Always present once resolved — falls back to the action label. */
  confirmVerb: string;
}

/**
 * Resolve the effective confirm config for a destructive-capable list action.
 * Returns `null` when the action should fire immediately (non-destructive, or
 * explicitly opted out), or a fully-populated {@link ResolvedConfirm} when a
 * dialog should be shown.
 *
 * `selectedCount` is supplied for bulk actions so the synthesized default copy
 * reads "{label} {n} items?"; pass `undefined` for single-row actions.
 */
function resolveActionConfirm(
  action: { label: string; destructive?: boolean; confirm?: ListActionConfirm | false; skipConfirm?: boolean },
  selectedCount?: number,
): ResolvedConfirm | null {
  // Explicit opt-out wins over everything — fire immediately, no double-prompt.
  if (action.confirm === false || action.skipConfirm) return null;

  // Explicit confirm config → use it verbatim (richer dialog, back-compat).
  if (action.confirm) {
    return { ...action.confirm, confirmVerb: action.confirm.confirmVerb ?? action.label };
  }

  // No confirm supplied: destructive ⇒ auto-confirm; otherwise fire immediately.
  if (!action.destructive) return null;

  const noun =
    selectedCount != null
      ? `${selectedCount} item${selectedCount === 1 ? '' : 's'}`
      : null;
  return {
    title: noun ? `${action.label} ${noun}?` : `${action.label}?`,
    description: "This can't be undone.",
    confirmVerb: action.label,
    destructive: true,
  };
}

// ──────────────────────────────────────────────────────────────────
// Bulk-action button — routes confirm-gated bulk actions through the
// shared ConfirmActionDialog before firing `onRun`.
// ──────────────────────────────────────────────────────────────────

interface BulkActionButtonProps {
  action: ListBulkAction;
  selectedIds: string[];
  testId: string;
}

function BulkActionButton({ action, selectedIds, testId }: BulkActionButtonProps) {
  const [confirming, setConfirming] = React.useState(false);
  // Resolved each render so the synthesized "{n} items?" copy tracks the
  // current selection size.
  const resolved = resolveActionConfirm(action, selectedIds.length);

  const fire = () => {
    if (resolved) {
      setConfirming(true);
    } else {
      void action.onRun(selectedIds);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant={action.destructive ? 'destructive' : 'outline'}
        className="h-7"
        onClick={fire}
        data-testid={testId}
      >
        {action.icon ?? null}
        {action.label}
      </Button>

      {confirming && resolved ? (
        <ConfirmActionDialog
          open
          title={resolved.title}
          description={resolved.description}
          confirmVerb={resolved.confirmVerb}
          destructive={resolved.destructive ?? action.destructive ?? true}
          reason={resolved.reason}
          reasonLabel={resolved.reasonLabel}
          onConfirm={(detail) => {
            setConfirming(false);
            void action.onRun(selectedIds, { reason: detail.reason });
          }}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
    </>
  );
}

// ──────────────────────────────────────────────────────────────────
// Permission-denied banner
// ──────────────────────────────────────────────────────────────────

export interface ListArchetypePermissionDeniedProps {
  title?: string;
  message?: string;
  className?: string;
}

export function ListArchetypePermissionDenied({
  title = 'You do not have permission to view this list.',
  message,
  className,
}: ListArchetypePermissionDeniedProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed p-12 text-center',
        className,
      )}
      data-testid="permission-denied"
      role="status"
      aria-label="Permission denied"
    >
      <Lock className="h-10 w-10 text-muted-foreground" aria-hidden />
      <p className="text-base font-medium">{title}</p>
      {message ? <p className="text-sm text-muted-foreground">{message}</p> : null}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Per-row kebab — wraps a DropdownMenu with row-scoped quick actions
// ──────────────────────────────────────────────────────────────────

export interface ListArchetypeRowActionsProps {
  rowId: string;
  actions: ListRowAction[];
  label?: string;
}

export function ListArchetypeRowActions({
  rowId,
  actions,
  label = 'Row actions',
}: ListArchetypeRowActionsProps) {
  // Action awaiting confirmation, paired with its resolved dialog copy. Set
  // when a confirm-gated (explicit OR auto-default-destructive) action is
  // picked; the shared ConfirmActionDialog renders until the operator
  // confirms/cancels.
  const [pending, setPending] = React.useState<{
    action: ListRowAction;
    confirm: ResolvedConfirm;
  } | null>(null);

  if (actions.length === 0) return null;

  const runAction = (a: ListRowAction) => {
    // Single-row actions pass `undefined` count → "{label}?" (no item count).
    const confirm = resolveActionConfirm(a, undefined);
    if (confirm) {
      setPending({ action: a, confirm });
    } else {
      void a.onRun(rowId);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={(e) => e.stopPropagation()}
            aria-label={label}
            data-testid={`row-actions-${rowId}`}
          >
            <MoreHorizontal className="h-4 w-4" aria-hidden />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
          {actions.map((a) => (
            <DropdownMenuItem
              key={a.id}
              onClick={() => runAction(a)}
              className={a.destructive ? 'text-destructive' : undefined}
              data-testid={`row-action-${a.id}-${rowId}`}
            >
              {a.icon ?? null}
              {a.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      {pending ? (
        <ConfirmActionDialog
          open
          title={pending.confirm.title}
          description={pending.confirm.description}
          confirmVerb={pending.confirm.confirmVerb}
          destructive={pending.confirm.destructive ?? pending.action.destructive ?? true}
          reason={pending.confirm.reason}
          reasonLabel={pending.confirm.reasonLabel}
          onConfirm={(detail) => {
            const a = pending.action;
            setPending(null);
            void a.onRun(rowId, { reason: detail.reason });
          }}
          onCancel={() => setPending(null)}
        />
      ) : null}
    </>
  );
}

/** Trash-icon helper for the common delete row action. */
export const ListArchetypeDeleteIcon = () => (
  <Trash2 className="mr-2 h-3.5 w-3.5" aria-hidden />
);
